import { Capacitor } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const IS_NATIVE = Capacitor.isNativePlatform();

// On native, mirror the JWT into the secure store so the SwiftUI viewer
// (same shared access group) can read it. The account key 'ds_token' is
// aligned with the viewer's APIClient key.
let _sharedConfigured = false;
async function configureShared() {
  if (!IS_NATIVE || _sharedConfigured) return;
  try {
    await SecureStorage.setKeyPrefix('');
    await SecureStorage.setSynchronize(false);
    _sharedConfigured = true;
  } catch (e) { /* plugin unavailable mid-build — fall back to localStorage */ }
}

let _token = localStorage.getItem('ds_token');

export function setToken(t) {
  _token = t;
  if (t) localStorage.setItem('ds_token', t);
  else localStorage.removeItem('ds_token');
  if (IS_NATIVE) {
    configureShared().then(() => {
      if (t) SecureStorage.set('ds_token', t).catch(() => {});
      else SecureStorage.remove('ds_token').catch(() => {});
    });
  }
}

export function getToken() { return _token; }

/// Called from AuthProvider on app mount. If the viewer logged in on this
/// device, its JWT is already in the shared store — pull it into localStorage
/// and return it so the admin UI skips the login screen.
export async function loadSharedToken() {
  if (!IS_NATIVE || _token) return _token;
  try {
    await configureShared();
    const v = await SecureStorage.get('ds_token');
    if (typeof v === 'string' && v) {
      _token = v;
      localStorage.setItem('ds_token', v);
      return v;
    }
  } catch {}
  return null;
}

export async function apiFetch(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...(_token ? { Authorization: `Bearer ${_token}` } : {}),
    ...(options.headers || {}),
  };
  const res  = await fetch(`${API_URL}${path}`, { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export const api = {
  login:          (role)           => apiFetch('/auth/dev-login', { method:'POST', body: JSON.stringify({ role }) }),
  getArtists:     async ()         => { const d = await apiFetch('/artists'); return Array.isArray(d) ? { artists: d } : d; },
  getSheets:      async (artistId) => { const d = await apiFetch(`/sheets?artistId=${artistId}`); return Array.isArray(d) ? { sheets: d } : d; },
  createSheet:    (body)           => apiFetch('/sheets', { method:'POST', body: JSON.stringify(body) }),
  copyToArtists:  (id, artistIds)  => apiFetch(`/sheets/${id}/copy-to-artists`, { method:'POST', body: JSON.stringify({ artistIds }) }),
  updateSheet:    (id, body)       => apiFetch(`/sheets/${id}`, { method:'PATCH', body: JSON.stringify(body) }),
  publishSheet:   (id)             => apiFetch(`/sheets/${id}/publish`, { method:'POST' }),
  unpublishSheet: (id)             => apiFetch(`/sheets/${id}/unpublish`, { method:'POST' }),
  getTeam:         (artistId)        => apiFetch(`/artists/${artistId}/team`),
  addTeamMember:   (artistId, body)  => apiFetch(`/artists/${artistId}/team`, { method:'POST', body: JSON.stringify(body) }),
  deleteTeamMember:(artistId, memberId) => apiFetch(`/artists/${artistId}/team/${memberId}`, { method:'DELETE' }),
  uploadFile:      (file)             => apiFetch('/uploads', { method:'POST', body: JSON.stringify(file) }),
  getTravel:       (artistId, date)   => apiFetch(`/travel?artistId=${artistId}&date=${date}`),
  getAllTravel:     (artistId)         => apiFetch(`/travel/artist/${artistId}`),
  createTravel:    (body)             => apiFetch('/travel', { method:'POST', body: JSON.stringify(body) }),
  updateTravel:    (id, body)         => apiFetch(`/travel/${id}`, { method:'PATCH', body: JSON.stringify(body) }),
  deleteTravel:    (id)               => apiFetch(`/travel/${id}`, { method:'DELETE' }),
  getEvents:      async (daysheetId) => { const d = await apiFetch(`/events?daysheetId=${daysheetId}`); return Array.isArray(d) ? { events: d } : d; },
  getEventsByArtist: async (artistId, date) => { let p = `/events?artistId=${artistId}`; if (date) p += `&date=${date}`; const d = await apiFetch(p); return Array.isArray(d) ? { events: d } : d; },
  createEvent:    (body)           => apiFetch('/events', { method:'POST', body: JSON.stringify(body) }),
  bulkEvents:     (body)           => apiFetch('/events/bulk', { method:'POST', body: JSON.stringify(body) }),
  updateEvent:    (id, body)       => apiFetch(`/events/${id}`, { method:'PATCH', body: JSON.stringify(body) }),
  deleteEvent:    (id)             => apiFetch(`/events/${id}`, { method:'DELETE' }),
  linkEvent:      (eventId, sheetId) => apiFetch('/events/link', { method:'POST', body: JSON.stringify({ eventId, sheetId }) }),
  unlinkEvent:    (eventId, sheetId) => apiFetch('/events/unlink', { method:'POST', body: JSON.stringify({ eventId, sheetId }) }),
  pushToGCal:     (eventId, calendarId) => apiFetch(`/events/${eventId}/push-to-gcal`, { method:'POST', body: JSON.stringify({ calendarId }) }),
  repeatEvent:    (id, body)       => apiFetch(`/events/${id}/repeat`, { method:'POST', body: JSON.stringify(body) }),
  flightLookup:   (flight, date)   => apiFetch(`/travel/flight-lookup?flight=${encodeURIComponent(flight)}${date?'&date='+date:''}`),
  tailLookup:     (tail)           => apiFetch(`/travel/tail-lookup?tail=${encodeURIComponent(tail)}`),
  gcalTravel:     (artistId, date) => apiFetch(`/travel/gcal-travel/${artistId}?date=${date}`),
  parseGcalEvent: (body)           => apiFetch('/travel/parse-gcal-event', { method:'POST', body: JSON.stringify(body) }),
  deleteSheet:    (id)             => apiFetch(`/sheets/${id}`, { method:'DELETE' }),
  getEventLibrary: async (artistId) => { const d = await apiFetch(`/events/library?artistId=${artistId}`); return Array.isArray(d) ? d : []; },
  getCalendars:   async (artistId) => { const d = await apiFetch(`/calendar/list/${artistId}`); return Array.isArray(d) ? { calendars: d } : d; },
  getCalEvents:   async (artistId, date) => { const d = await apiFetch(`/calendar/events/${artistId}?date=${date}`); return Array.isArray(d) ? { events: d } : d; },
  placesAuto:     (input)          => apiFetch(`/places/autocomplete?input=${encodeURIComponent(input)}`),
  selectCalendars: (artistId, ids)  => apiFetch(`/calendar/select/${artistId}`, { method:"PATCH", body: JSON.stringify({ calendarIds: ids }) }),
  sendEmail:      (sheetId, groupIds, extraEmails) => apiFetch(`/email/send/${sheetId}`, { method:'POST', body: JSON.stringify({ groupIds, extraEmails }) }),
  previewEmail:   (sheetId)        => apiFetch(`/email/preview/${sheetId}`),
  agentGenerate:  (body)           => apiFetch('/agent/generate', { method:'POST', body: JSON.stringify(body) }),
  agentGenerateAuto: (body)       => apiFetch('/agent/generate-auto', { method:'POST', body: JSON.stringify(body) }),
  agentApprove:   (sheetId, body)  => apiFetch(`/agent/approve/${sheetId}`, { method:'POST', body: JSON.stringify(body || {}) }),
  // Broadcasts — admin announcements pushed to viewers across one or
  // more artist channels. Multi-artist body shape: { artistIds: [], headline, message, imageUrl? }
  listBroadcasts:   (artistId)       => apiFetch(`/broadcasts${artistId ? `?artistId=${artistId}` : ''}`),
  createBroadcast:  (body)           => apiFetch('/broadcasts', { method:'POST', body: JSON.stringify(body) }),
  deleteBroadcast:  (id)             => apiFetch(`/broadcasts/${id}`, { method:'DELETE' }),
};
